import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { ComboboxOption } from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { useMemo, useState } from "react";

import type { TokenDataSource } from "../tokens/types";

/**
 * Searchable option lists for the resource-id collectors (forms, form groups, files, managed
 * content, snippets). Each list is fetched lazily — only the query matching the active
 * `dataSource` is enabled — with server-side search driven by the collector's search box. Only
 * descriptors are read; no authenticated link or credential is ever resolved here.
 *
 * The option `value` is exactly what the token build function needs: a record id for resource
 * links, or the snippet `key` for snippet tokens.
 */
export const useTokenResourceOptions = (dataSource: TokenDataSource | undefined) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const searchRequest = debouncedSearch ? { query: debouncedSearch } : undefined;
  const baseRequest = { limit: 20, search: searchRequest };

  const formsQuery = useTellescopeInfiniteQuery({
    model: "forms",
    method: "getSome",
    request: baseRequest,
    enabled: dataSource === "forms",
    select: (data) => data.pages.flat(),
  });
  const formGroupsQuery = useTellescopeInfiniteQuery({
    model: "form_groups",
    method: "getSome",
    request: baseRequest,
    enabled: dataSource === "form_groups",
    select: (data) => data.pages.flat(),
  });
  const filesQuery = useTellescopeInfiniteQuery({
    model: "files",
    method: "getSome",
    request: baseRequest,
    enabled: dataSource === "files",
    select: (data) => data.pages.flat(),
  });
  const contentQuery = useTellescopeInfiniteQuery({
    model: "managed_content_records",
    method: "getSome",
    request: baseRequest,
    enabled: dataSource === "content",
    select: (data) => data.pages.flat(),
  });
  const snippetsQuery = useTellescopeInfiniteQuery({
    model: "message_template_snippets",
    method: "getSome",
    request: baseRequest,
    enabled: dataSource === "snippets",
    select: (data) => data.pages.flat(),
  });

  const options = useMemo<ComboboxOption[]>(() => {
    switch (dataSource) {
      case "forms":
        return (formsQuery.data ?? []).map((form) => ({ value: form.id, label: form.title }));
      case "form_groups":
        return (formGroupsQuery.data ?? []).map((group) => ({ value: group.id, label: group.title }));
      case "files":
        return (filesQuery.data ?? []).map((file) => ({ value: file.id, label: file.name }));
      case "content":
        return (contentQuery.data ?? []).map((record) => ({ value: record.id, label: record.title }));
      case "snippets":
        return (snippetsQuery.data ?? []).map((snippet) => ({ value: snippet.key, label: snippet.key }));
      default:
        return [];
    }
  }, [dataSource, formsQuery.data, formGroupsQuery.data, filesQuery.data, contentQuery.data, snippetsQuery.data]);

  const isLoading =
    (dataSource === "forms" && formsQuery.isLoading) ||
    (dataSource === "form_groups" && formGroupsQuery.isLoading) ||
    (dataSource === "files" && filesQuery.isLoading) ||
    (dataSource === "content" && contentQuery.isLoading) ||
    (dataSource === "snippets" && snippetsQuery.isLoading);

  return { options, search, setSearch, isLoading };
};
